import imageUrlBuilder from '@sanity/image-url';
import type { SanityImageSource } from '@sanity/image-url/lib/types/types';
import type { SanityImageData } from '@/types/sanity';
import { client } from './sanity';

const builder = imageUrlBuilder(client);

/** Widths (px) requested from Sanity's image pipeline when building a `srcset`. */
export const DEFAULT_IMAGE_WIDTHS = [320, 480, 640, 960, 1280] as const;

const DEFAULT_QUALITY = 75;

/** High-quality tier for the image viewer: wide and sharp, requested only on demand. */
const HQ_WIDTH = 2400;
const HQ_QUALITY = 90;

export function urlFor(source: SanityImageSource) {
  return builder.image(source);
}

/** GROQ returns `null` for missing crop/hotspot; the URL builder expects `undefined`. */
function toSource(image: SanityImageData): SanityImageSource {
  return {
    asset: image.asset,
    crop: image.crop ?? undefined,
    hotspot: image.hotspot ?? undefined,
  };
}

/**
 * URL of a resized copy of the image.
 *
 * - `width` + `fit=max`: never upscale beyond the original.
 * - `auto=format`: Sanity serves WebP/AVIF to browsers that support it.
 */
export function imageSrc(
  image: SanityImageData,
  width: number,
  quality: number = DEFAULT_QUALITY
): string {
  return builder
    .image(toSource(image))
    .width(width)
    .fit('max')
    .auto('format')
    .quality(quality)
    .url();
}

/** Sharper, larger copy for the image viewer. Never used for grids or cards. */
export function imageHqSrc(image: SanityImageData): string {
  return imageSrc(image, HQ_WIDTH, HQ_QUALITY);
}

/** `srcset` value with one candidate per requested width. */
export function imageSrcSet(
  image: SanityImageData,
  widths: readonly number[] = DEFAULT_IMAGE_WIDTHS
): string {
  return widths.map((width) => `${imageSrc(image, width)} ${width}w`).join(', ');
}

export function fileUrlFor(source?: { asset?: { url?: string | null } | null } | null): string {
  return source?.asset?.url ?? '';
}
